/**
 * URL-backed time range state for the GHL middleware dashboard.
 *
 * Replaces `useState<TimeRange>(defaultTimeRange)` with a hook that reads
 * from / writes to the URL search string. This means:
 *
 * - Refreshing the page preserves the selected range.
 * - Switching tabs (whose URLs change) preserves the range when the
 *   navigation is done via paths that carry the search string forward.
 * - Sharing a URL gives the recipient the same filtered view.
 *
 * URL params (all written together, all required):
 *   ?from=<ISO>&until=<ISO>&range_label=<string>
 *
 * If any are missing or malformed on load, falls back to `defaultTimeRange()`.
 *
 * Drop-in shape: returns `[timeRange, setTimeRange]` so the consumer call
 * site only needs to swap `useState<TimeRange>(defaultTimeRange)` →
 * `useUrlTimeRange()`. The setter merges with existing URL params (preserves
 * `page`, `action`, etc.) and uses `{ replace: true }` so updates don't
 * pollute browser back-button history.
 */

import { useCallback, useMemo, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import type { TimeRange } from '@/components/ghl-dashboard'

/**
 * The default range when no URL params are present. Computed once at mount
 * via `useState`'s initializer so the "now" timestamp doesn't slide on every
 * render.
 */
export function defaultTimeRange(): TimeRange {
  return {
    from: new Date(Date.now() - 24 * 60 * 60 * 1000),
    until: new Date(),
    label: 'Last 1 day',
  }
}

export function useUrlTimeRange(): [TimeRange, (range: TimeRange) => void] {
  const [searchParams, setSearchParams] = useSearchParams()

  // Anchor the default to first mount. Without this, every render would
  // produce a new Date object → new TimeRange reference → cascade of
  // unnecessary re-fetches in consuming hooks.
  const [defaultRange] = useState<TimeRange>(defaultTimeRange)

  const timeRange = useMemo<TimeRange>(() => {
    const from = searchParams.get('from')
    const until = searchParams.get('until')
    const label = searchParams.get('range_label')

    if (from && until) {
      const fromDate = new Date(from)
      const untilDate = new Date(until)
      const valid =
        !Number.isNaN(fromDate.getTime()) &&
        !Number.isNaN(untilDate.getTime()) &&
        fromDate.getTime() <= untilDate.getTime()
      if (valid) {
        return {
          from: fromDate,
          until: untilDate,
          // Label is purely for the UI button — fall back to a generic
          // marker if the URL was hand-edited and dropped it.
          label: label ?? 'Custom',
        }
      }
    }

    return defaultRange
  }, [searchParams, defaultRange])

  const setTimeRange = useCallback(
    (range: TimeRange) => {
      setSearchParams(
        (prev) => {
          // Clone so we don't mutate the caller's URLSearchParams. Other
          // params (action=, etc.) on the same URL stay intact.
          const next = new URLSearchParams(prev)
          next.set('from', range.from.toISOString())
          next.set('until', range.until.toISOString())
          next.set('range_label', range.label)
          // Reset pagination — a new time range invalidates page positions
          // ("page 5 of 1" when the result set shrinks), and folding this
          // into the same setSearchParams call avoids a navigate-vs-navigate
          // race with any caller-side `setPage(0)` that would otherwise
          // overwrite the from/until/range_label we just wrote (each
          // setSearchParams updater reads the live URL, not the in-flight
          // one, so the last call in the tick wins).
          next.delete('page')
          return next
        },
        { replace: true }
      )
    },
    [setSearchParams]
  )

  return [timeRange, setTimeRange]
}
